import { computed, ref, watch, type Ref } from 'vue'
import SensorResource from '@/api/resources/sensor'
import type { ISensor } from '@/interfaces/entities/sensor'
import type { ISensorWithSchema } from '@/utils/sensor-schema-fields'
import {
  buildSensorSchemaFieldOptionsFromKeys,
  intersectSensorSchemaFieldKeys,
} from '@/utils/sensor-schema-fields'

const detailsCache = new Map<number, ISensorWithSchema>()

export async function fetchSensorDetailsByType(
  sensorId: number,
  sensorType: string,
): Promise<ISensorWithSchema | null> {
  const cached = detailsCache.get(sensorId)
  if (cached) return cached

  try {
    let response: ISensorWithSchema | null = null
    switch (sensorType) {
      case 'electricity':
        response = (await SensorResource.getEnergySensorsById(sensorId)) as ISensorWithSchema
        break
      case 'temperature':
        response = (await SensorResource.getTemperatureSensorsById(sensorId)) as ISensorWithSchema
        break
      case 'door_open':
        response = (await SensorResource.getOpenSensorsById(sensorId)) as ISensorWithSchema
        break
      default:
        return null
    }

    if (response) {
      detailsCache.set(sensorId, response)
    }
    return response
  } catch {
    return null
  }
}

export function clearWidgetModalSensorDetailsCache(): void {
  detailsCache.clear()
}

export function useWidgetModalSensorDetails() {
  const selectedSensor = ref<ISensor | null>(null)
  const sensorDetails = ref<ISensorWithSchema | null>(null)
  const isLoadingDetails = ref(false)
  const detailsError = ref(false)
  const unsupportedSensorType = ref(false)

  async function loadDetailsForSensor(sensor: ISensor | null): Promise<void> {
    if (!sensor) {
      sensorDetails.value = null
      isLoadingDetails.value = false
      detailsError.value = false
      unsupportedSensorType.value = false
      return
    }

    const supportedTypes = ['electricity', 'temperature', 'door_open']
    if (!supportedTypes.includes(sensor.sensor_type)) {
      sensorDetails.value = null
      isLoadingDetails.value = false
      detailsError.value = false
      unsupportedSensorType.value = true
      return
    }

    unsupportedSensorType.value = false
    isLoadingDetails.value = true
    detailsError.value = false

    try {
      const details = await fetchSensorDetailsByType(sensor.id, sensor.sensor_type)
      sensorDetails.value = details
      detailsError.value = !details
    } catch {
      sensorDetails.value = null
      detailsError.value = true
    } finally {
      isLoadingDetails.value = false
    }
  }

  function onSensorSelected(
    sensor: ISensor,
    setFieldValue: (field: string, value: unknown) => void,
  ): void {
    selectedSensor.value = sensor
    setFieldValue('sensorId', String(sensor.id))
    setFieldValue('dataField', '')
    void loadDetailsForSensor(sensor)
  }

  async function syncSelectedSensorFromEdit(
    sensor: ISensor | null,
  ): Promise<void> {
    selectedSensor.value = sensor
    await loadDetailsForSensor(sensor)
  }

  return {
    selectedSensor,
    sensorDetails,
    isLoadingDetails,
    detailsError,
    unsupportedSensorType,
    loadDetailsForSensor,
    onSensorSelected,
    syncSelectedSensorFromEdit,
  }
}

const MULTI_SUPPORTED_SENSOR_TYPES = ['electricity', 'temperature', 'door_open']

export function useWidgetModalMultiSensorDetails(selectedSensors: Ref<ISensor[]>) {
  const sensorDetailsMap = ref<Map<number, ISensorWithSchema>>(new Map())
  const isLoadingDetails = ref(false)
  const detailsError = ref(false)
  const unsupportedSensorType = ref(false)

  async function loadDetailsForSensors(sensors: ISensor[]): Promise<void> {
    if (sensors.length === 0) {
      sensorDetailsMap.value = new Map()
      isLoadingDetails.value = false
      detailsError.value = false
      unsupportedSensorType.value = false
      return
    }

    const hasUnsupported = sensors.some((s) => !MULTI_SUPPORTED_SENSOR_TYPES.includes(s.sensor_type))
    if (hasUnsupported) {
      sensorDetailsMap.value = new Map()
      isLoadingDetails.value = false
      detailsError.value = false
      unsupportedSensorType.value = true
      return
    }

    unsupportedSensorType.value = false
    isLoadingDetails.value = true
    detailsError.value = false

    try {
      const results = await Promise.all(
        sensors.map(async (sensor) => {
          const details = await fetchSensorDetailsByType(sensor.id, sensor.sensor_type)
          return { sensorId: sensor.id, details }
        }),
      )

      const nextMap = new Map<number, ISensorWithSchema>()
      let hasError = false

      results.forEach(({ sensorId, details }) => {
        if (details) {
          nextMap.set(sensorId, details)
        } else {
          hasError = true
        }
      })

      sensorDetailsMap.value = nextMap
      detailsError.value = hasError
    } catch {
      sensorDetailsMap.value = new Map()
      detailsError.value = true
    } finally {
      isLoadingDetails.value = false
    }
  }

  watch(
    selectedSensors,
    (sensors) => {
      void loadDetailsForSensors(sensors)
    },
    { immediate: true, deep: true },
  )

  const commonFieldKeys = computed(() => {
    const detailsList = selectedSensors.value.map(
      (sensor) => sensorDetailsMap.value.get(sensor.id) ?? null,
    )
    return intersectSensorSchemaFieldKeys(detailsList)
  })

  function buildFieldOptions(
    t: (path: string) => string,
    te?: (path: string) => boolean,
  ) {
    return buildSensorSchemaFieldOptionsFromKeys(commonFieldKeys.value, t, te)
  }

  return {
    sensorDetailsMap,
    isLoadingDetails,
    detailsError,
    unsupportedSensorType,
    commonFieldKeys,
    buildFieldOptions,
    loadDetailsForSensors,
  }
}
